/**
 * CustomerFitnessView.jsx
 * Admin component to view customer's fitness tracking data
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Activity, Heart, Moon, Footprints, Flame, TrendingUp,
  TrendingDown, Minus, Dumbbell, RefreshCw, User,
  Shield, ShieldOff, BarChart3, Calendar
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CustomerFitnessView = ({ customerId, customerName }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    if (customerId) {
      loadFitnessData();
    }
  }, [customerId, days]);

  const loadFitnessData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API}/fitness-tracking/admin/customer/${customerId}?days=${days}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(res.data);
    } catch (error) {
      console.error('Error loading fitness data:', error);
      toast.error('Fehler beim Laden der Fitness-Daten');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <RefreshCw className="w-6 h-6 animate-spin text-yellow-500" />
      </div>
    );
  }

  // Not shared
  if (!data?.shared) {
    return (
      <Card className="bg-gray-50 border-gray-200">
        <CardContent className="p-8 text-center">
          <ShieldOff className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Keine Daten-Freigabe</h3>
          <p className="text-gray-500">
            {customerName || 'Der Kunde'} hat die Fitness-Daten nicht freigegeben.
          </p>
        </CardContent>
      </Card>
    );
  }

  const { daily_data, workouts, summary, sharing_settings } = data;

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'improving':
        return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'declining':
        return <TrendingDown className="w-4 h-4 text-red-500" />;
      default:
        return <Minus className="w-4 h-4 text-zinc-400" />;
    }
  };

  const getTrendText = (trend) => {
    switch (trend) {
      case 'improving':
        return 'Verbesserung';
      case 'declining':
        return 'Rückgang';
      default:
        return 'Stabil';
    }
  };

  return (
    <div className="space-y-4" data-testid="customer-fitness-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Activity className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Fitness Tracking</h3>
          <Badge variant="outline" className="border-green-500/50 text-green-600">
            <Shield className="w-3 h-3 mr-1" />
            Freigegeben
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value))}
            className="bg-white border border-gray-300 rounded-lg px-3 py-1 text-sm text-gray-900"
          >
            <option value={7}>7 Tage</option>
            <option value={14}>14 Tage</option>
            <option value={30}>30 Tage</option>
            <option value={90}>90 Tage</option>
          </select>
          <Button variant="ghost" size="sm" onClick={loadFitnessData}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {sharing_settings.steps && (
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-1">
                <Footprints className="w-4 h-4 text-blue-600" />
                <span className="text-xs text-blue-600">Ø Schritte/Tag</span>
              </div>
              <p className="text-xl font-bold text-blue-900">
                {summary.avg_daily_steps?.toLocaleString() || 0}
              </p>
            </CardContent>
          </Card>
        )}

        {sharing_settings.calories && (
          <Card className="bg-orange-50 border-orange-200">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-1">
                <Flame className="w-4 h-4 text-orange-600" />
                <span className="text-xs text-orange-600">Ø Kalorien/Tag</span>
              </div>
              <p className="text-xl font-bold text-orange-900">
                {summary.avg_daily_calories?.toLocaleString() || 0}
              </p>
            </CardContent>
          </Card>
        )}

        {sharing_settings.workouts && (
          <Card className="bg-yellow-50 border-yellow-200">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-1">
                <Dumbbell className="w-4 h-4 text-yellow-600" />
                <span className="text-xs text-yellow-600">Workouts</span>
              </div>
              <p className="text-xl font-bold text-yellow-900">
                {summary.total_workouts || 0}
              </p>
            </CardContent>
          </Card>
        )}

        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              {getTrendIcon(summary.trend)}
              <span className="text-xs text-green-600">Trend</span>
            </div>
            <p className="text-xl font-bold text-green-900">
              {getTrendText(summary.trend)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Shared Data Info */}
      <div className="flex flex-wrap gap-2">
        <span className="text-xs text-gray-500">Freigegebene Daten:</span>
        {sharing_settings.steps && <Badge variant="secondary" className="text-xs">Schritte</Badge>}
        {sharing_settings.calories && <Badge variant="secondary" className="text-xs">Kalorien</Badge>}
        {sharing_settings.workouts && <Badge variant="secondary" className="text-xs">Workouts</Badge>}
        {sharing_settings.sleep && <Badge variant="secondary" className="text-xs">Schlaf</Badge>}
        {sharing_settings.heart_rate && <Badge variant="secondary" className="text-xs">Herzfrequenz</Badge>}
        {sharing_settings.weight && <Badge variant="secondary" className="text-xs">Gewicht</Badge>}
      </div>

      {/* Recent Activity */}
      {daily_data.length > 0 && (
        <Card className="bg-white border-gray-200">
          <CardHeader className="py-3">
            <CardTitle className="text-sm text-gray-900">Letzte Aktivitäten</CardTitle>
          </CardHeader>
          <CardContent className="py-0">
            <div className="divide-y divide-gray-100">
              {daily_data.slice(0, 5).map((day, idx) => (
                <div key={idx} className="py-2 flex items-center justify-between text-sm">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-700">{day.date}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    {sharing_settings.steps && day.steps && (
                      <span className="text-blue-600">{day.steps?.toLocaleString()} Schritte</span>
                    )}
                    {sharing_settings.calories && day.calories_burned && (
                      <span className="text-orange-600">{day.calories_burned} kcal</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Workouts */}
      {workouts.length > 0 && (
        <Card className="bg-white border-gray-200">
          <CardHeader className="py-3">
            <CardTitle className="text-sm text-gray-900">Letzte Workouts</CardTitle>
          </CardHeader>
          <CardContent className="py-0">
            <div className="divide-y divide-gray-100">
              {workouts.slice(0, 5).map((workout, idx) => (
                <div key={idx} className="py-2 flex items-center justify-between text-sm">
                  <div className="flex items-center gap-3">
                    <Dumbbell className="w-4 h-4 text-yellow-600" />
                    <div>
                      <span className="text-gray-900">{workout.workout_type}</span>
                      <span className="text-gray-400 ml-2 text-xs">{workout.date}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-gray-500">
                    <span>{workout.duration_minutes} min</span>
                    {workout.calories_burned && (
                      <span className="text-orange-600">{workout.calories_burned} kcal</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Data */}
      {daily_data.length === 0 && workouts.length === 0 && (
        <Card className="bg-gray-50 border-gray-200">
          <CardContent className="p-6 text-center">
            <Activity className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">
              Keine Fitness-Daten im gewählten Zeitraum
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CustomerFitnessView;
